import {
  type AdminModels,
  CSRF_HEADER,
  EFFORTS,
  type Effort,
  type FamilyResolutionView,
  familyIdOf,
  familyVersions,
  type InferenceGeography,
  MODEL_CATALOGUE,
  MODEL_FAMILIES,
  MODEL_LABS,
  meetsRoleNeeds,
  PRESET_REASONING_LEVELS,
  type PresetReasoning,
  reasoningLevelsOf,
  type ScopeLabView,
  type ScopeModelHealth,
  type SettingsCatalogue,
  type SettingsCatalogueEntry,
  type VersionHealthView,
  type VersionSwitchHistory,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  CircleCheck,
  CircleDashed,
  CircleX,
  Globe,
  History,
  Lock,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ApiError, ok } from "../api-errors.ts";
import { formatDate, formatDateTime } from "../i18n/format.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { adminKeys } from "./entity-tree.ts";

/**
 * The backoffice Models screen's sections (openspec change `model-picking`, D4, D7, D10, D15,
 * D17, slice 5): the labs the level offers (the tenant's opt-in with the untested warning), the
 * family and reasoning level each effort runs on, version pins, and health with its switch
 * history. Display names only: never an account, connection or profile ID.
 */

const EFFORT_ROLE = { fast: "fast", balanced: "chat", deep: "reasoning" } as const;

/** What every section reads: the level's health view, its settings and its switch history. */
export function useModelsData(level: string | null) {
  const { admin } = useAdminApi();
  const query = level ? { entityId: level } : {};
  const health = useQuery({
    queryKey: ["admin", "models", "health", level ?? "tenant"],
    queryFn: async (): Promise<ScopeModelHealth> => ok(await admin["model-health"].$get({ query })),
    retry: false,
  });
  const catalogue = useQuery({
    queryKey: adminKeys.catalogue(level),
    queryFn: async (): Promise<SettingsCatalogue> =>
      ok(await admin.settings.catalogue.$get({ query: level ? { entity: level } : {} })),
    retry: false,
  });
  const switches = useQuery({
    queryKey: ["admin", "models", "switches", level ?? "tenant"],
    queryFn: async (): Promise<VersionSwitchHistory> =>
      ok(await admin["model-switches"].$get({ query })),
    retry: false,
  });
  return { health, catalogue, switches };
}

/** One model setting at the level, as the settings catalogue states it. */
function entryOf(catalogue: SettingsCatalogue | undefined, key: string) {
  return catalogue?.settings.find((s) => s.key === key);
}

/** Writes one model setting at the level (tenant or entity), with its lock. */
type SettingWrite = { value: unknown; locked: boolean; acknowledgeUntested?: boolean };

/** One settings write at a level, without refreshing anything (the caller does). */
function useSettingPut(level: string | null) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  return async (key: string, json: SettingWrite) => {
    const headers = { headers: { [CSRF_HEADER]: csrfToken } };
    return level === null
      ? ok(await admin.settings[":key"].$put({ param: { key }, json }, headers))
      : ok(
          await admin.entities[":entityId"].settings[":key"].$put(
            { param: { entityId: level, key }, json },
            headers,
          ),
        );
  };
}

/** Refreshes what a model setting write changes. */
function useModelSettingsRefresh() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "models"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] }),
      queryClient.invalidateQueries({ queryKey: ["model-options"] }),
    ]);
}

function useModelSettingWrite(level: string | null, key: string) {
  const put = useSettingPut(level);
  const refresh = useModelSettingsRefresh();
  return useMutation({
    mutationFn: (json: SettingWrite) => put(key, json),
    onSettled: refresh,
  });
}

/** Where a value comes from, and whether it is locked: the provenance line of a section. */
function Provenance({ entry }: { entry: SettingsCatalogueEntry | undefined }) {
  const { t } = useTranslation();
  if (!entry) {
    return null;
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
      <span>
        {entry.ownValue
          ? t("models.sections.setHere")
          : t("models.sections.inheritedFrom", {
              source: entry.sourceName ?? t(`models.source.${entry.source}`),
            })}
      </span>
      {entry.lockedBy ? (
        <span className="inline-flex items-center gap-1">
          <Lock aria-hidden="true" className="size-3" />
          {t("models.lockedAbove")}
        </span>
      ) : entry.lockedHere ? (
        <span className="inline-flex items-center gap-1">
          <Lock aria-hidden="true" className="size-3" />
          {t("models.sections.lockedHere")}
        </span>
      ) : null}
    </p>
  );
}

/** Lock checkbox, Save and its error: the footer of every editable section. */
function SaveBar(props: {
  locked: boolean;
  onLock: (on: boolean) => void;
  /** The label of the lock (default "Lock for levels below"). */
  lockLabel?: string;
  /** The lock's setting is locked above: shown, not changeable. */
  lockDisabled?: boolean;
  /** A second lock in the same footer (a section saving two settings). */
  extraLock?: {
    label: string;
    locked: boolean;
    onLock: (on: boolean) => void;
    disabled?: boolean;
  };
  disabled: boolean;
  pending: boolean;
  onSave: () => void;
  error: string | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-lock`}
          checked={props.locked}
          disabled={props.lockDisabled}
          onCheckedChange={(on) => props.onLock(on === true)}
        />
        <Label htmlFor={`${id}-lock`} className="font-normal text-fg-secondary">
          {props.lockLabel ?? t("models.lock")}
        </Label>
      </div>
      {props.extraLock ? (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${id}-lock-extra`}
            checked={props.extraLock.locked}
            disabled={props.extraLock.disabled}
            onCheckedChange={(on) => props.extraLock?.onLock(on === true)}
          />
          <Label htmlFor={`${id}-lock-extra`} className="font-normal text-fg-secondary">
            {props.extraLock.label}
          </Label>
        </div>
      ) : null}
      <Button size="sm" variant="outline" disabled={props.disabled} onClick={props.onSave}>
        {props.pending ? t("models.saving") : t("models.save")}
      </Button>
      {props.error ? <InlineError>{props.error}</InlineError> : null}
    </div>
  );
}

const CONTAINER = "divide-y divide-line rounded-lg border border-line bg-surface-raised";

function Loading({ error }: { error: unknown }) {
  return error ? (
    <Notice tone="danger">{error instanceof Error ? error.message : String(error)}</Notice>
  ) : (
    <Skeleton className="h-64 w-full" />
  );
}

// ——— Labs ———————————————————————————————————————————————————————————————————————————————

/** A lab's state in words with an icon (never colour alone), the badge when tested. */
export function LabState({ lab }: { lab: ScopeLabView }) {
  const { t } = useTranslation();
  if (lab.state === "tested") {
    return (
      <span className="inline-flex h-5 max-w-full items-center gap-1 rounded-[5px] border border-line bg-surface-sunken px-1.5 text-xs font-medium text-fg-secondary">
        <ShieldCheck aria-hidden="true" className="size-3 shrink-0 text-success-fg" />
        <span className="truncate">
          {lab.evaluation
            ? t("models.sections.labs.tested", {
                date: formatDate(lab.evaluation.date),
                version: lab.evaluation.catalogueVersion,
              })
            : t("models.sections.labs.testedNoDate")}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-secondary">
      <CircleDashed aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {t("models.sections.labs.untested")}
    </span>
  );
}

/**
 * The labs the level offers (D4, D17): at tenant level every lab the instance has not blocked,
 * with its state, the Tested by ESI badge, and the opt-in (an untested lab only after the
 * warning, acknowledged); offered labs first, the primary at the top, reordered by keyboard;
 * a lab with no route in the scope's geographies says it is not available in the EU. Entities
 * narrow among the labs their parent offers.
 */
export function LabsSection(props: {
  level: string | null;
  data: ReturnType<typeof useModelsData>;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { health, catalogue } = props.data;
  const entry = entryOf(catalogue.data, "models.labs");
  if (!health.data || !catalogue.data || !entry) {
    return <Loading error={health.error ?? catalogue.error} />;
  }
  return (
    <LabsEditor
      key={JSON.stringify(entry.value)}
      level={props.level}
      labs={health.data.labs ?? []}
      entry={entry}
      editable={!props.readOnly && entry.lockedBy === null}
    />
  );
}

function LabsEditor(props: {
  level: string | null;
  labs: ScopeLabView[];
  entry: SettingsCatalogueEntry;
  editable: boolean;
}) {
  const { t } = useTranslation();
  const stored = (props.entry.value as string[]) ?? [];
  const known = new Set(props.labs.map((l) => l.lab));
  // A lab the instance has since blocked stays stored (an unblock restores it) but is not
  // shown: the editor orders the shown ones and keeps the hidden ones after them on save.
  const hidden = stored.filter((l) => !known.has(l));
  const shown = stored.filter((l) => known.has(l));
  const [value, setValue] = useState<string[]>(shown);
  const [locked, setLocked] = useState(props.entry.lockedHere);
  const [warning, setWarning] = useState<string[] | null>(null);
  const write = useModelSettingWrite(props.level, "models.labs");
  const byId = new Map(props.labs.map((l) => [l.lab, l]));
  // Offered first, in their order (the first is the primary lab), then the others.
  const offered = value;
  const others = props.labs.filter((l) => !value.includes(l.lab));
  const changed = JSON.stringify(value) !== JSON.stringify(shown);
  const added = value.filter((l) => !stored.includes(l));
  const untestedAdded = added.filter((l) => byId.get(l)?.state === "untested");
  const save = (acknowledgeUntested: boolean) =>
    write.mutate(
      {
        value: [...value, ...hidden],
        locked,
        ...(acknowledgeUntested && { acknowledgeUntested: true }),
      },
      { onSuccess: () => setWarning(null) },
    );
  const move = (lab: string, by: -1 | 1) => {
    const i = value.indexOf(lab);
    const j = i + by;
    if (i < 0 || j < 0 || j >= value.length) {
      return;
    }
    const next = [...value];
    [next[i], next[j]] = [next[j] as string, next[i] as string];
    setValue(next);
  };
  const refusal =
    write.error instanceof ApiError && write.error.code === "untested_lab_unacknowledged"
      ? t("models.sections.labs.unacknowledged")
      : (write.error?.message ?? null);
  return (
    <div className="flex flex-col gap-2">
      <ul className={CONTAINER} aria-label={t("models.sections.labs.offeredList")}>
        {offered.map((id, i) => {
          const lab = byId.get(id) as ScopeLabView;
          return (
            <LabRow key={id} lab={lab} primary={i === 0}>
              {props.editable ? (
                <div className="flex items-center gap-1">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t("models.sections.labs.moveUp", { lab: lab.displayName })}
                    disabled={i === 0}
                    onClick={() => move(id, -1)}
                  >
                    <ArrowUp aria-hidden="true" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t("models.sections.labs.moveDown", { lab: lab.displayName })}
                    disabled={i === offered.length - 1}
                    onClick={() => move(id, 1)}
                  >
                    <ArrowDown aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    // At least one lab stays offered.
                    disabled={offered.length === 1}
                    onClick={() => setValue(value.filter((l) => l !== id))}
                  >
                    {t("models.sections.labs.drop")}
                  </Button>
                </div>
              ) : null}
            </LabRow>
          );
        })}
        {others.map((lab) => (
          <LabRow key={lab.lab} lab={lab} primary={false}>
            {props.editable ? (
              <Button size="sm" variant="outline" onClick={() => setValue([...value, lab.lab])}>
                {t("models.sections.labs.offer")}
              </Button>
            ) : null}
          </LabRow>
        ))}
        {props.editable ? (
          <li>
            <SaveBar
              locked={locked}
              onLock={setLocked}
              disabled={(!changed && locked === props.entry.lockedHere) || write.isPending}
              pending={write.isPending}
              onSave={() => (untestedAdded.length > 0 ? setWarning(untestedAdded) : save(false))}
              error={warning ? null : refusal}
            />
          </li>
        ) : null}
      </ul>
      <Provenance entry={props.entry} />
      <UntestedDialog
        labs={(warning ?? []).map((l) => byId.get(l)?.displayName ?? l)}
        open={warning !== null}
        pending={write.isPending}
        error={warning ? refusal : null}
        onClose={() => setWarning(null)}
        onConfirm={() => save(true)}
      />
    </div>
  );
}

function LabRow(props: { lab: ScopeLabView; primary: boolean; children?: ReactNode }) {
  const { t } = useTranslation();
  const { lab } = props;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-fg">{lab.displayName}</span>
          {props.primary ? (
            <span className="text-xs text-fg-muted">{t("models.sections.labs.primary")}</span>
          ) : null}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <LabState lab={lab} />
          {lab.availableHere ? null : (
            <span className="inline-flex items-center gap-1 text-xs text-fg-secondary">
              <Globe aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
              {t("models.sections.labs.notInEu")}
            </span>
          )}
        </span>
      </div>
      {props.children}
    </li>
  );
}

/**
 * The warning before an untested lab is offered (D17, verbatim copy), with an explicit
 * acknowledgement: the API refuses the write without it, so a client cannot skip it.
 */
function UntestedDialog(props: {
  labs: string[];
  open: boolean;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [confirmed, setConfirmed] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: each opening needs its own confirmation
  useEffect(() => setConfirmed(false), [props.open]);
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("models.sections.labs.dialog.title", {
        count: props.labs.length,
        labs: props.labs.join(", "),
      })}
      description={t("models.sections.labs.dialog.lead")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (confirmed && !props.pending) {
            props.onConfirm();
          }
        }}
      >
        <p className="flex items-start gap-2 rounded-md bg-warning-bg px-3 py-2.5 text-sm text-fg">
          <TriangleAlert
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-warning-fg"
            strokeWidth={1.75}
          />
          <span>{t("models.sections.labs.warning")}</span>
        </p>
        <p className="text-sm text-fg-secondary">{t("models.sections.labs.dialog.scope")}</p>
        <div className="flex items-start gap-2.5">
          <Checkbox
            id={`${id}-ack`}
            checked={confirmed}
            onCheckedChange={(on) => setConfirmed(on === true)}
            className="mt-0.5"
          />
          <Label htmlFor={`${id}-ack`} className="font-normal leading-snug">
            {t("models.sections.labs.dialog.confirm")}
          </Label>
        </div>
        {props.error ? <Notice tone="danger">{props.error}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("settings.cancel")}
          </Button>
          <Button type="submit" disabled={!confirmed || props.pending}>
            {t("models.sections.labs.dialog.submit", { count: props.labs.length })}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

// ——— Efforts ————————————————————————————————————————————————————————————————————————————

/** The families of the offered labs, primary lab first, catalogue order within a lab. */
function offeredFamilies(labs: readonly string[]) {
  return labs.flatMap((lab) => Object.values(MODEL_FAMILIES).filter((f) => f.lab === lab));
}

/** Whether any catalogued version of a family can serve an effort's role (the gate, D4). */
function familyServes(family: string, effort: Effort): boolean {
  return Object.keys(MODEL_CATALOGUE).some(
    (key) => familyIdOf(key) === family && meetsRoleNeeds(key, EFFORT_ROLE[effort]),
  );
}

/** A version's health in words with an icon (never colour alone). */
function HealthWords({ version }: { version: VersionHealthView | undefined }) {
  const { t } = useTranslation();
  if (!version || version.status === null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
        <CircleDashed aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("models.sections.health.notProbed")}
      </span>
    );
  }
  const good = version.status === "ok" || version.status === "throttled";
  const Icon = good ? CircleCheck : version.status === "unknown" ? CircleDashed : CircleX;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs",
        good
          ? "text-fg-secondary"
          : version.status === "unknown"
            ? "text-fg-muted"
            : "text-danger-fg",
      )}
    >
      <Icon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {t(`models.sections.health.status.${version.status}`)}
      {version.probedAt ? (
        <span className="text-fg-muted">
          {" · "}
          {t("models.sections.health.probedAt", { date: formatDateTime(version.probedAt) })}
        </span>
      ) : null}
    </span>
  );
}

/** The family each effort runs on (D4, D10): resolved version, availability and health. */
export function EffortFamiliesSection(props: {
  level: string | null;
  data: ReturnType<typeof useModelsData>;
  readOnly: boolean;
}) {
  const { health, catalogue } = props.data;
  const entry = entryOf(catalogue.data, "models.effortFamilies");
  const reasoning = entryOf(catalogue.data, "models.effortReasoning");
  const labs = entryOf(catalogue.data, "models.labs");
  if (!health.data || !entry || !reasoning || !labs) {
    return <Loading error={health.error ?? catalogue.error} />;
  }
  return (
    <EffortFamiliesEditor
      key={JSON.stringify([entry.value, reasoning.value])}
      level={props.level}
      entry={entry}
      reasoning={reasoning}
      labs={(labs.value as string[]) ?? []}
      families={health.data.families}
      editable={!props.readOnly && entry.lockedBy === null}
      reasoningEditable={!props.readOnly && reasoning.lockedBy === null}
    />
  );
}

function EffortFamiliesEditor(props: {
  level: string | null;
  entry: SettingsCatalogueEntry;
  reasoning: SettingsCatalogueEntry;
  labs: string[];
  families: FamilyResolutionView[];
  editable: boolean;
  reasoningEditable: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const stored = (props.entry.value as Partial<Record<Effort, string>>) ?? {};
  const storedLevels = (props.reasoning.value as Partial<Record<Effort, PresetReasoning>>) ?? {};
  const [value, setValue] = useState(stored);
  const [levels, setLevels] = useState(storedLevels);
  // Two locks (Q19): the families, and the reasoning levels, whose lock also turns off the
  // per-conversation level below; locking one never locks the other.
  const [locked, setLocked] = useState(props.entry.lockedHere);
  const [levelsLocked, setLevelsLocked] = useState(props.reasoning.lockedHere);
  const put = useSettingPut(props.level);
  const refresh = useModelSettingsRefresh();
  const families = offeredFamilies(props.labs);
  const familiesChanged =
    JSON.stringify(value) !== JSON.stringify(stored) || locked !== props.entry.lockedHere;
  const levelsChanged =
    JSON.stringify(levels) !== JSON.stringify(storedLevels) ||
    levelsLocked !== props.reasoning.lockedHere;
  const changed = familiesChanged || levelsChanged;
  // One save for both settings, refreshed once both are written: the editor stays mounted, so a
  // failed second write keeps its error and the unsaved selection on screen.
  const save = useMutation({
    mutationFn: async () => {
      if (props.editable && familiesChanged) {
        await put("models.effortFamilies", { value, locked });
      }
      if (props.reasoningEditable && levelsChanged) {
        await put("models.effortReasoning", { value: levels, locked: levelsLocked });
      }
    },
    onSuccess: refresh,
  });
  const pending = save.isPending;
  // The family an effort resolves to here: its mapped one, else the server's view (default).
  const resolvedOf = (effort: Effort) => {
    const role = EFFORT_ROLE[effort];
    const view = props.families.find(
      (f) => f.family === (value[effort] ?? defaultFamilyOf(effort, props.families)),
    );
    return { role, view };
  };
  return (
    <div className="flex flex-col gap-2">
      <ul className={CONTAINER}>
        {EFFORTS.map((effort) => {
          const { view } = resolvedOf(effort);
          const chosen = value[effort] ?? "";
          const serves = chosen === "" || familyServes(chosen, effort);
          const version = view?.versions.find((v) => v.model === view.model);
          return (
            <li key={effort} className="flex flex-col gap-2 px-4 py-3 @container">
              <div className="flex flex-col gap-2 @xl:flex-row @xl:items-center @xl:justify-between">
                <span className="text-sm font-medium text-fg">
                  {t(`models.picker.efforts.${effort}.name`)}
                </span>
                <div className="grid gap-2 @md:grid-cols-[minmax(0,1fr)_13rem] @xl:w-[36rem]">
                  <NativeSelect
                    id={`${id}-${effort}`}
                    aria-label={`${t(`models.picker.efforts.${effort}.name`)}, ${t("models.sections.efforts.family")}`}
                    value={chosen}
                    disabled={!props.editable}
                    onChange={(e) => {
                      const next = { ...value };
                      if (e.currentTarget.value === "") {
                        delete next[effort];
                      } else {
                        next[effort] = e.currentTarget.value;
                      }
                      setValue(next);
                    }}
                  >
                    <option value="">{t("models.sections.efforts.default")}</option>
                    {families.map((f) => (
                      <option key={f.id} value={f.id} disabled={!familyServes(f.id, effort)}>
                        {props.labs.length > 1
                          ? `${f.displayName} (${labName(f.lab)})`
                          : f.displayName}
                        {familyServes(f.id, effort)
                          ? ""
                          : ` — ${t("models.sections.efforts.notUsable", { effort: t(`models.picker.efforts.${effort}.name`) })}`}
                      </option>
                    ))}
                  </NativeSelect>
                  <NativeSelect
                    id={`${id}-${effort}-reasoning`}
                    aria-label={`${t(`models.picker.efforts.${effort}.name`)}, ${t("models.sections.efforts.reasoning")}`}
                    value={levels[effort] ?? "default"}
                    disabled={!props.reasoningEditable}
                    onChange={(e) => {
                      const next = { ...levels };
                      const level = e.currentTarget.value as PresetReasoning;
                      if (level === "default") {
                        delete next[effort];
                      } else {
                        next[effort] = level;
                      }
                      setLevels(next);
                    }}
                  >
                    {PRESET_REASONING_LEVELS.map((l) => (
                      <option key={l} value={l}>
                        {t(`models.sections.efforts.reasoningLevels.${l}`)}
                        {!view?.model || reasoningLevelsOf(view.model).includes(l)
                          ? ""
                          : ` — ${t("models.sections.efforts.notSupported", { model: displayName(view.model) })}`}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              </div>
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-secondary">
                {serves ? null : (
                  <span className="inline-flex items-center gap-1 text-warning-fg">
                    <TriangleAlert aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                    {t("models.sections.efforts.notUsableTools", {
                      effort: t(`models.picker.efforts.${effort}.name`),
                    })}
                  </span>
                )}
                {/* Nothing answers (#401): never "Now" a version that does not answer; the
                    version kept for when it answers again shows with its health. */}
                {view?.model && !nothingAnswers(view) ? (
                  <span>
                    {t("models.sections.efforts.resolves", {
                      model: displayName(view.model),
                      provider: providerLabel(view.provider, t),
                    })}
                  </span>
                ) : view ? (
                  <NothingAnswers view={view} />
                ) : null}
                {view?.model && nothingAnswers(view) ? (
                  <span>{displayName(view.model)}</span>
                ) : null}
                {view?.model ? <HealthWords version={version} /> : null}
              </p>
            </li>
          );
        })}
        {props.editable || props.reasoningEditable ? (
          <li>
            {" "}
            <SaveBar
              locked={locked}
              onLock={setLocked}
              lockLabel={t("models.sections.efforts.lockFamilies")}
              lockDisabled={!props.editable}
              extraLock={{
                label: t("models.sections.efforts.lockReasoning"),
                locked: levelsLocked,
                onLock: setLevelsLocked,
                disabled: !props.reasoningEditable,
              }}
              disabled={!changed || pending}
              pending={pending}
              onSave={() => save.mutate()}
              error={save.error?.message ?? null}
            />
          </li>
        ) : null}
      </ul>
      <Provenance entry={props.entry} />
    </div>
  );
}

/** The family an effort uses without a mapping: the server's first view of that tier. */
function defaultFamilyOf(effort: Effort, families: FamilyResolutionView[]): string | undefined {
  const tier = { fast: "fast", balanced: "balanced", deep: "deep" }[effort];
  return families.find(
    (f) => (MODEL_FAMILIES as Record<string, { tier: string }>)[f.family]?.tier === tier,
  )?.family;
}

function displayName(key: string): string {
  return (MODEL_CATALOGUE as Record<string, { displayName: string }>)[key]?.displayName ?? key;
}

function labName(lab: string): string {
  return (MODEL_LABS as Record<string, { displayName: string }>)[lab]?.displayName ?? lab;
}

function providerLabel(
  provider: string,
  t: (key: "models.picker.providers.esi" | "models.picker.providers.tenantBedrock") => string,
) {
  return provider === "esi"
    ? t("models.picker.providers.esi")
    : provider === "tenant:bedrock"
      ? t("models.picker.providers.tenantBedrock")
      : provider;
}

// ——— Pins ———————————————————————————————————————————————————————————————————————————————

/**
 * Version pins (D7, D10): per family of the offered labs, Latest or a catalogued version,
 * newest first, grouped by lab; a version no connection lists says "not available on
 * <provider>", and a pin not honoured says so.
 */
export function PinsSection(props: {
  level: string | null;
  data: ReturnType<typeof useModelsData>;
  models: AdminModels;
  readOnly: boolean;
}) {
  const { health, catalogue } = props.data;
  const entry = entryOf(catalogue.data, "models.versionPin");
  const labs = entryOf(catalogue.data, "models.labs");
  if (!health.data || !entry || !labs) {
    return <Loading error={health.error ?? catalogue.error} />;
  }
  return (
    <PinsEditor
      key={JSON.stringify(entry.value)}
      level={props.level}
      entry={entry}
      labs={(labs.value as string[]) ?? []}
      geographies={props.models.geographies}
      families={health.data.families}
      editable={!props.readOnly && entry.lockedBy === null}
    />
  );
}

function PinsEditor(props: {
  level: string | null;
  entry: SettingsCatalogueEntry;
  labs: string[];
  geographies: readonly InferenceGeography[];
  families: FamilyResolutionView[];
  editable: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const stored = (props.entry.value as Record<string, string>) ?? {};
  const [value, setValue] = useState(stored);
  const [locked, setLocked] = useState(props.entry.lockedHere);
  const write = useModelSettingWrite(props.level, "models.versionPin");
  const changed = JSON.stringify(value) !== JSON.stringify(stored);
  const families = offeredFamilies(props.labs);
  return (
    <div className="flex flex-col gap-2">
      <ul className={CONTAINER}>
        {families.map((family) => {
          const versions = [
            ...new Set(props.geographies.flatMap((g) => familyVersions(family.id, g))),
          ];
          if (versions.length === 0) {
            return null;
          }
          const view = props.families.find((f) => f.family === family.id);
          const listed = (key: string) =>
            view?.versions.find((v) => v.model === key)?.listed !== false;
          const pin = value[family.id] ?? "";
          return (
            <li key={family.id} className="flex flex-col gap-1.5 px-4 py-3 @container">
              <div className="flex flex-col gap-2 @xl:flex-row @xl:items-center @xl:justify-between">
                <Label htmlFor={`${id}-${family.id}`} className="text-sm font-medium text-fg">
                  {props.labs.length > 1
                    ? `${family.displayName} · ${labName(family.lab)}`
                    : family.displayName}
                </Label>
                <NativeSelect
                  id={`${id}-${family.id}`}
                  className="@xl:max-w-xs"
                  value={pin}
                  disabled={!props.editable}
                  onChange={(e) => {
                    const next = { ...value };
                    if (e.currentTarget.value === "") {
                      delete next[family.id];
                    } else {
                      next[family.id] = e.currentTarget.value;
                    }
                    setValue(next);
                  }}
                >
                  <option value="">{t("models.sections.pins.latest")}</option>
                  {versions.map((key) => (
                    <option key={key} value={key}>
                      {displayName(key)}
                      {listed(key)
                        ? ""
                        : ` — ${t("models.sections.pins.notAvailableOn", { provider: providerLabel(view?.provider ?? "esi", t) })}`}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              {view?.resolution === "pin_unhonoured" &&
              pin === stored[family.id] &&
              pin !== "" &&
              nothingAnswers(view) ? (
                <NothingAnswers view={view} />
              ) : view?.resolution === "pin_unhonoured" &&
                pin === stored[family.id] &&
                pin !== "" ? (
                <p className="flex items-center gap-1 text-xs text-warning-fg">
                  <TriangleAlert aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                  {t("models.sections.pins.unhonoured", {
                    pin: displayName(pin),
                    model: view.model
                      ? displayName(view.model)
                      : t("models.sections.efforts.noVersion"),
                  })}
                </p>
              ) : null}
            </li>
          );
        })}
        {props.editable ? (
          <li>
            <SaveBar
              locked={locked}
              onLock={setLocked}
              disabled={(!changed && locked === props.entry.lockedHere) || write.isPending}
              pending={write.isPending}
              onSave={() => write.mutate({ value, locked })}
              error={write.error?.message ?? null}
            />
          </li>
        ) : null}
      </ul>
      <Provenance entry={props.entry} />
    </div>
  );
}

/** Probe results that mean a version does not answer. */
const FAILING = new Set(["denied", "unavailable", "error", "retired"]);

/**
 * No version of the family answers here (#401): nothing healthy (`none`, the version shown is
 * kept for when it answers again, or none was ever resolved), or a pin not honoured whose
 * fallback fails too.
 */
function nothingAnswers(view: FamilyResolutionView): boolean {
  if (view.resolution === "none") {
    return true;
  }
  const resolved = view.versions.find((v) => v.model === view.model);
  return view.resolution === "pin_unhonoured" && FAILING.has(resolved?.status ?? "");
}

/** Says it when nothing answers (#401): the kept version, or none at all. */
function NothingAnswers({ view }: { view: FamilyResolutionView }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-xs text-warning-fg">
      <TriangleAlert aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {t(view.model ? "models.sections.efforts.noneKept" : "models.sections.efforts.noVersion")}
    </span>
  );
}

/** How long a queued refresh shows as running before health and history are read again. */
const REFRESH_SETTLE_MS = 90_000;

// ——— Health and history ———————————————————————————————————————————————————————————————

/**
 * Health and history (D6, D8, D10): per family, its versions with whether a connection lists
 * them and their last probe (or "not probed yet"), the scope's `model.version_switched`
 * history, and the admin's Refresh models (discover, then probe every listed version;
 * rate-limited).
 */
export function HealthSection(props: {
  level: string | null;
  data: ReturnType<typeof useModelsData>;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const { health, switches } = props.data;
  const refresh = useMutation({
    mutationFn: async () =>
      ok(
        await admin["model-health"].refresh.$post(
          { query: props.level ? { entityId: props.level } : {} },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["admin", "models", "health"] }),
    // The run is queued, not done: read health and history again once it has had time, then
    // let the server's cooldown decide when Refresh is offered again.
    onSuccess: () => {
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ["admin", "models", "health"] });
        void queryClient.invalidateQueries({ queryKey: ["admin", "models", "switches"] });
        refresh.reset();
      }, REFRESH_SETTLE_MS);
    },
  });
  if (!health.data) {
    return <Loading error={health.error} />;
  }
  const wait = health.data.checkNowAfterSeconds;
  // One row per family, in its first geography (the scope's preferred one).
  const seen = new Set<string>();
  const families = health.data.families.filter((f) => !seen.has(f.family) && seen.add(f.family));
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        {props.readOnly ? null : (
          <Button
            size="sm"
            variant="outline"
            disabled={refresh.isPending || refresh.isSuccess || wait > 0}
            onClick={() => refresh.mutate()}
          >
            <RefreshCw aria-hidden="true" />
            {t("models.sections.health.refresh")}
          </Button>
        )}
        <span className="text-xs text-fg-muted" role="status">
          {refresh.isSuccess
            ? t("models.sections.health.refreshing")
            : wait > 0
              ? t("models.sections.health.waitMinutes", { count: Math.ceil(wait / 60) })
              : t(`models.sections.health.interval.${health.data.interval}`)}
        </span>
        {refresh.isError ? <InlineError>{refresh.error.message}</InlineError> : null}
      </div>
      <ul className={CONTAINER}>
        {families.map((f) => (
          <li key={f.family} className="flex flex-col gap-1.5 px-4 py-3">
            <span className="text-sm font-medium text-fg">
              {(MODEL_FAMILIES as Record<string, { displayName: string }>)[f.family]?.displayName ??
                f.family}
            </span>
            {/* Nothing answers (#401): said, never silent; the version in bold is the one kept. */}
            {nothingAnswers(f) ? <NothingAnswers view={f} /> : null}
            <ul className="grid gap-1">
              {f.versions.map((v) => (
                <li
                  key={`${v.model}-${v.geography}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px]"
                >
                  <span
                    className={cn(
                      "inline-flex min-w-40 items-center gap-1.5",
                      v.model === f.model ? "font-medium text-fg" : "text-fg-secondary",
                    )}
                  >
                    {displayName(v.model)}
                    {/* A resolved version known to fail is only still tried (nothing answers). */}
                    {v.model === f.model && !FAILING.has(v.status ?? "") ? (
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 font-normal text-fg-secondary"
                      >
                        {t("models.sections.health.inUse")}
                      </Badge>
                    ) : null}
                  </span>
                  {v.listed === false ? (
                    <span className="text-xs text-fg-muted">
                      {t("models.sections.health.notListed", {
                        provider: providerLabel(f.provider, t),
                      })}
                    </span>
                  ) : (
                    <HealthWords version={v} />
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <section aria-labelledby="models-history" className="flex flex-col gap-2">
        <h3 id="models-history" className="flex items-center gap-1.5 text-sm font-semibold text-fg">
          <History aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
          {t("models.sections.health.history")}
        </h3>
        {(switches.data?.switches ?? []).length === 0 ? (
          <p className="text-sm text-fg-secondary">{t("models.sections.health.noHistory")}</p>
        ) : (
          <ul className={CONTAINER}>
            {(switches.data?.switches ?? []).slice(0, 8).map((s) => (
              <li
                key={`${s.at}-${s.family}-${s.geography}`}
                className="flex flex-col gap-0.5 px-4 py-2.5"
              >
                <span className="text-[13px] text-fg">
                  {t("models.sections.health.switch", {
                    from: s.from ? displayName(s.from) : t("models.sections.health.noVersion"),
                    to: s.to ? displayName(s.to) : t("models.sections.health.noVersion"),
                  })}
                </span>
                <span className="text-xs text-fg-muted">
                  {formatDate(s.at)} · {t(`models.sections.health.reasons.${s.reason}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * The stored allowed-models list (5.10b): what the level stores, and a warning when it names
 * only versions the catalogue keeps as older (such a list never follows a new version).
 */
export function AllowedModelsLine({ models }: { models: AdminModels }) {
  const { t } = useTranslation();
  const setting = models.policy.allowedModels;
  if (!setting.olderOnly) {
    return null;
  }
  return (
    <Notice tone="warning">
      {t("models.sections.olderOnly", {
        models: (setting.stored ?? []).map(displayName).join(", "),
      })}
    </Notice>
  );
}
